import { Fragment } from 'react'

/** One horizontal bar inside a ranked row. */
export interface RankedBar {
  /** Bar colour (any CSS colour). */
  color: string
  /** 0–1 share of the longest bar in this column. */
  fraction: number
  /** Text shown at the end of the bar, e.g. "₹14Cr" or "380 lines". */
  label: string
}

/** One ranked row: a name plus one or more bars. */
export interface RankedBarItem {
  key: string
  label: string
  bars: RankedBar[]
}

interface RankedBarListProps {
  items: RankedBarItem[]
  /** Called with the row's key when a row is clicked (drill-down). */
  onSelect?: (key: string) => void
  emptyText?: string
  /** Width of the name column (CSS length). Names longer than this truncate with a tooltip. */
  labelWidth?: string
  /** Width of the value at the end of each bar (CSS length); every row shares it, so the bars line up. */
  valueWidth?: string
}

/**
 * Ranked horizontal bars as a list instead of a Recharts axis chart.
 *
 * A `<BarChart layout="vertical">` with five suppliers spends most of its
 * width on a 100px tick column that truncates every name to "Jeh Aerospace
 * P…", wraps three-line labels into each other, and puts the value on a
 * gridline axis the eye has to travel to. A ranked list gives every row its
 * rank, the full name (ellipsised with the full text on hover), a bar for
 * relative size, and the exact value at the end of the bar — readable at a
 * glance and the same shape for counts and money.
 *
 * @param items - rows in rank order; each carries one or more bars
 * @param onSelect - row click handler (drill-down), receives the row key
 * @param emptyText - shown when there are no rows
 * @param labelWidth - CSS width of the name column (default 14rem)
 * @param valueWidth - CSS width of each bar's value (default 6rem)
 */
export function RankedBarList({ items, onSelect, emptyText = 'No data for the current filters.', labelWidth = '14rem', valueWidth = '6rem' }: RankedBarListProps) {
  if (items.length === 0) {
    return <div className="h-[120px] flex items-center justify-center text-sm text-muted-foreground">{emptyText}</div>
  }
  const clickable = typeof onSelect === 'function'
  return (
    <div className="flex flex-col divide-y divide-border/60" role="list">
      {items.map((item, index) => (
        <Fragment key={item.key}>
          <div
            role={clickable ? 'button' : 'listitem'}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onSelect(item.key) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(item.key) } } : undefined}
            className={`grid items-center gap-3 py-2 px-1 rounded-md ${clickable ? 'cursor-pointer hover:bg-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring' : ''}`}
            style={{ gridTemplateColumns: `1.5rem ${labelWidth} minmax(0, 1fr)` }}
            title={item.label}
          >
            <span className="text-xs text-muted-foreground tabular-nums text-right">{index + 1}</span>
            <span className="text-sm font-medium truncate">{item.label}</span>
            <div className="flex flex-col gap-1 min-w-0">
              {item.bars.map((bar, barIndex) => (
                <div key={barIndex} className="flex items-center gap-2 min-w-0">
                  <div className="flex-1 h-3.5 rounded-sm bg-muted/70 overflow-hidden min-w-0">
                    <div
                      className="h-full rounded-sm transition-[width] duration-300"
                      style={{ width: `${Math.max(0, Math.min(1, bar.fraction)) * 100}%`, backgroundColor: bar.color }}
                    />
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground whitespace-nowrap text-right shrink-0" style={{ width: valueWidth }}>{bar.label}</span>
                </div>
              ))}
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  )
}
